import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { tableFeatureLabel } from "@/lib/table-features";
import {
  type TableConstraintsIssue,
  useTableConstraintsOverrideStore,
} from "@/store/table-constraints-override-store";
import type { ApiTableFeature } from "@/types/api";

function renderIssue(issue: TableConstraintsIssue): React.ReactNode {
  switch (issue.kind) {
    case "wasteful":
      return (
        <span>
          Las mesas elegidas tienen capacidad <strong>{issue.total_max}</strong> para{" "}
          <strong>{issue.pax} pax</strong>. Hay una combinación más chica que también funciona —
          considera reducir el conjunto.
        </span>
      );
    case "too_small":
      return (
        <span>
          Las mesas elegidas suman <strong>{issue.total_max}</strong> de capacidad máxima y la
          reserva es de <strong>{issue.pax} pax</strong>. No alcanzan.
        </span>
      );
    case "too_big":
      return (
        <span>
          La capacidad mínima del conjunto es <strong>{issue.total_min}</strong> y la reserva sólo
          es de <strong>{issue.pax} pax</strong>. Mesas muy grandes para tan pocas personas.
        </span>
      );
    case "missing_features":
      return (
        <span>
          Mesas sin las features pedidas (
          {issue.required.map((f) => tableFeatureLabel(f as ApiTableFeature)).join(", ")}):
          <ul className="mt-1 ml-4 list-disc text-sm">
            {Object.entries(issue.missing_by_table).map(([name, missing]) => (
              <li key={name}>
                <strong>{name}</strong> — falta{" "}
                {missing.map((f) => tableFeatureLabel(f as ApiTableFeature)).join(", ")}
              </li>
            ))}
          </ul>
        </span>
      );
    case "out_of_preferred_zone":
      return (
        <span>
          Zona preferida: <strong>{issue.preferred_zone_name || "—"}</strong>. Fuera de zona:{" "}
          <strong>{issue.out_of_zone_table_names.join(", ")}</strong>.
        </span>
      );
  }
}

/** Renders an AlertDialog when the backend returns
 *  ``409 { code: "table_constraints_override_required" }`` on a
 *  reservation create / update. Lists every soft issue and lets the
 *  operator confirm or cancel. */
export function TableConstraintsOverrideDialog() {
  const pending = useTableConstraintsOverrideStore((s) => s.pending);
  const close = useTableConstraintsOverrideStore((s) => s.close);

  const issues = pending?.context.issues ?? [];

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) close(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Atención antes de guardar</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block">
              La selección de mesas activa{" "}
              {issues.length === 1 ? "una alerta" : `${issues.length} alertas`}:
            </span>
            <ul className="mt-2 flex flex-col gap-2 text-foreground">
              {issues.map((issue, idx) => (
                <li key={`${issue.kind}-${idx}`} className="text-sm leading-snug">
                  {renderIssue(issue)}
                </li>
              ))}
            </ul>
            <span className="mt-3 block text-xs text-muted-foreground">
              Puedes confirmar igual o cancelar y elegir otras mesas.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => close(true)}>Confirmar y guardar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
